import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { ChallengeStatusResponse } from './models/challenge-status.response';
import { MediaProcessingResponse } from './models/media-processing.response';
import { ConfigService, Endpoint } from '@shared/services/config';
import { ChallengeCriterionType } from './models/challenge-criterion-type.enum';
import { UserStatusResponse } from './models/user-status.response';
import { AccessToken, Bearer } from '../common/access-token';
import { HttpHeader } from '../common/http-header';
import { MediaUploadResponse } from './models/media-upload.response';
import { TimeUtil } from '@shared/utils/time.util';
import { MediaProcessingStatus } from './models/media-processing-status.enum';

@Injectable({
  providedIn: 'root',
})
export class CheckService {
  private readonly http = inject(HttpClient);

  private readonly identityRootUrl: string;
  private readonly checkUrl: string;

  /** This is the maximum time a polling activity is allowed to run. */
  public readonly MAX_POLL_TIME_MS = 30 * 1000;

  /** This is how long the client should wait between polling the server, so as to not DDOS it. */
  public readonly POLL_PAUSE_TIME_MS = 1000;

  constructor() {
    const configService = inject(ConfigService);

    this.identityRootUrl = configService.getEndpoint(Endpoint.Identity);
    this.checkUrl = this.identityRootUrl + '/check';
  }

  //#region Check polling
  private async runPolling(uploadResponse: MediaUploadResponse, checkToken: AccessToken, expiryTime: number) {
    while (true) {
      if (Date.now() >= expiryTime) {
        return undefined;
      }

      const remainingTime = expiryTime - Date.now();
      const timeout = Symbol();

      const response = await Promise.race([
        firstValueFrom(this.getMediaSampleProcessingResult(uploadResponse.href, uploadResponse.media_type, checkToken)),
        TimeUtil.wait(remainingTime, timeout),
      ]);

      if (response === timeout) {
        return undefined;
      }

      switch (response.status) {
        case MediaProcessingStatus.Created:
        case MediaProcessingStatus.Processing:
          await TimeUtil.wait(this.POLL_PAUSE_TIME_MS);
          continue;
        default:
          return response;
      }
    }
  }

  /**
   * Poll repeatedly up to the standardised time limit (~30 seconds) for a successful check result.
   *
   * @throws If polling expired.
   * @returns A processing result, which might be good or bad.
   */
  public async pollForCheckResult(uploadResponse: MediaUploadResponse, checkToken: AccessToken): Promise<MediaProcessingResponse> {
    const startTime = Date.now();
    const expiryTime = startTime + this.MAX_POLL_TIME_MS;

    const result = await this.runPolling(uploadResponse, checkToken, expiryTime);

    if (!result) {
      throw Error('Polling expired without success');
    }

    return result;
  }

  //#endregion Check polling

  /**
   * Upload biometrics for the registration route.
   *
   * @param file The media file to upload
   * @param mediaType The type of media file
   * @param checkToken The access token for the check server
   * @returns An upload observable with progress events
   */
  public uploadRegisterBiometrics(file: Blob, mediaType: ChallengeCriterionType, checkToken: AccessToken) {
    const endpoint = `${this.checkUrl}/register`;

    const body = this.createFormData(file, mediaType);

    return this.http.post<MediaUploadResponse>(endpoint, body, {
      headers: new HttpHeaders({
        [HttpHeader.Authorization]: Bearer(checkToken),
      }),
      reportProgress: true,
      observe: 'events',
      responseType: 'json',
    });
  }

  /**
   * Upload biometrics for the challenge route.
   *
   * @param file The media file to upload
   * @param mediaType The type of media file
   * @param challengeId The challenge ID to upload the file to
   * @param checkToken The access token for the check server
   * @returns An upload observable with progress events
   */
  public uploadChallengeBiometrics(file: Blob, mediaType: ChallengeCriterionType, challengeId: string, checkToken: AccessToken) {
    const endpoint = `${this.checkUrl}/challenges/${challengeId}`;

    const body = this.createFormData(file, mediaType);

    return this.http.post<MediaUploadResponse>(endpoint, body, {
      headers: new HttpHeaders({
        [HttpHeader.Authorization]: Bearer(checkToken),
      }),
      reportProgress: true,
      observe: 'events',
      responseType: 'json',
    });
  }

  public getMediaSampleProcessingResult(
    targetEndpoint: string,
    mediaType: string,
    checkToken: AccessToken,
  ): Observable<MediaProcessingResponse> {
    // targetEndpoint returned by the server will already include the complete path (including `/check`) so use identity root.
    const endpoint = `${this.identityRootUrl}${targetEndpoint}`;

    const options = {
      headers: new HttpHeaders({
        [HttpHeader.Authorization]: Bearer(checkToken),
        [HttpHeader.Accept]: mediaType,
      }),
    };

    return this.http.get<MediaProcessingResponse>(endpoint, options);
  }

  public getCheckResult(challengeId: string, checkToken: AccessToken) {
    const endpoint = `${this.checkUrl}/challenges/${challengeId}`;

    const options = {
      headers: new HttpHeaders({
        [HttpHeader.Authorization]: Bearer(checkToken),
        [HttpHeader.Accept]: 'application/vnd.io.idaas.check.api.challenge.status.token.v1+json',
      }),
    };

    return firstValueFrom(this.http.get<ChallengeStatusResponse>(endpoint, options));
  }

  public getUserStatus(userId: string, accessToken: AccessToken) {
    const endpoint = `${this.checkUrl}/users/${userId}`;

    const options = {
      headers: new HttpHeaders({
        [HttpHeader.Authorization]: Bearer(accessToken),
        [HttpHeader.Accept]: 'application/vnd.io.idaas.check.api.user.status.v1+json',
      }),
    };

    return firstValueFrom(this.http.get<UserStatusResponse>(endpoint, options));
  }

  private createFormData(blob: Blob, type: ChallengeCriterionType): FormData {
    const formData = new FormData();
    formData.append('sample', blob, blob['fileName']);
    formData.append('type', type);
    return formData;
  }
}
